import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import LoaderOverlay from "modules/layout/components/LoaderOverlay";
import routes from "routes/routes";
import { getAccountSessionToken } from "authentication/accountSession";
import { withReturnTo } from "../common/returnTo";

interface RequireAccountProps {
  children: React.ReactElement;
}

/**
 * Guard for pages that need a logged-in account. Logged-in is derived from
 * profile presence in the account slice.
 *
 * State matrix:
 * - profile present → render children
 * - no profile, stored token, session check pending → loader (the app-shell
 *   init hook is restoring the session via GET /me)
 * - otherwise → redirect to the login step, carrying the current location as
 *   returnTo so the user lands back here after logging in
 */
const RequireAccount: React.FC<RequireAccountProps> = ({ children }) => {
  const location = useLocation();
  const profile = useSelector((state: RootState) => state.account.profile);
  const sessionChecked = useSelector(
    (state: RootState) => state.account.sessionChecked
  );

  if (profile) {
    return children;
  }

  if (getAccountSessionToken() && !sessionChecked) {
    return <LoaderOverlay open={true} />;
  }

  return (
    <Navigate
      to={withReturnTo(
        routes.accountLoginPath,
        location.pathname + location.search
      )}
      replace
    />
  );
};

export default RequireAccount;
